import { Editor } from "@monaco-editor/react";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import MultipleStopIcon from "@mui/icons-material/MultipleStop";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { isDefined } from "@mui/x-charts/internals";
import type React from "react";
import { useState } from "react";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { usePreSignedUrl } from "../hooks/use-pre-signed-url";
import { useTypedQueryParams } from "../hooks/use-query-params";
import {
  ACTION_INSTRUCTION_ACTIONS,
  type ActionInstruction,
  type ActionType,
  type Block,
  type LocatorStep,
} from "../types";
import { humanizeText } from "../utils/text";
import { ActionSelector } from "./action-selector";

const ACTION_LABEL_OVERRIDES: Partial<Record<ActionType, string>> = {
  ai_auto_close_popup: "AI Auto-close Pop-up",
  api_check: "API Check",
};

const getActionLabel = (option: ActionType) =>
  ACTION_LABEL_OVERRIDES[option] ?? humanizeText(option);

interface BlockEditorTabs {
  blockTab?: "info" | "screenshots";
}

interface DebugQuery {
  debug?: string;
}

interface BlockEditorProps {
  block: Block;
  onUpdate?: (updatedBlock: Block) => void;
  appConfigId?: string;
}

const ImageFromPath: React.FC<{ path: string; height?: string | number }> = ({
  path,
  height = "300px",
}) => {
  const { data, isLoading } = usePreSignedUrl(path);

  if (isLoading) {
    return (
      <Typography variant="body2" sx={{ fontSize: "14px", color: "text.secondary" }}>
        Loading image...
      </Typography>
    );
  }

  if (!data) {
    return (
      <Typography variant="body2" sx={{ fontSize: "14px", color: "text.secondary" }}>
        Image not found
      </Typography>
    );
  }

  return (
    <img
      src={data.url ?? "https://placehold.co/600x400"}
      alt="Screenshot"
      style={{
        display: "block",
        width: "100%",
        height: "auto",
        maxHeight: height,
        top: "0px",
        left: "0px",
        pointerEvents: "none",
        borderRadius: "6px",
        border: "1px solid rgba(0, 0, 0, 0.12)",
      }}
    />
  );
};

const aaaPhases = ["arrange", "act", "assert"] as const;

export const BlockEditor: React.FC<BlockEditorProps> = ({ block, onUpdate, appConfigId }) => {
  const [editedBlock, setEditedBlock] = useState<Block>(block);
  const [isModified, setIsModified] = useState(false);

  const { params: blockTabParams, setParam: setBlockTabParam } =
    useTypedQueryParams<BlockEditorTabs>();
  const blockTab = blockTabParams.blockTab ?? "info";

  const { params: debugParams, setParam: setDebugParam } = useTypedQueryParams<DebugQuery>();

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    setBlockTabParam("blockTab", newValue as BlockEditorTabs["blockTab"]);
  };

  const handleFieldChange = (field: keyof Block, value: unknown) => {
    console.log("handleFieldChange:", field, value);
    setEditedBlock((prev) => {
      const newBlock = { ...prev, [field]: value };
      console.log("Updated block:", newBlock);
      return newBlock;
    });
    setIsModified(true);
  };

  const handleLocatorStepChange = (stepIndex: number, field: keyof LocatorStep, value: unknown) => {
    const newChain = [...editedBlock.locator.chain];
    newChain[stepIndex] = { ...newChain[stepIndex], [field]: value };

    handleFieldChange("locator", {
      ...editedBlock.locator,
      chain: newChain,
    });
  };

  const handleKwargsChange = (stepIndex: number, key: string, value: string) => {
    const newChain = [...editedBlock.locator.chain];
    const currentKwargs = newChain[stepIndex].kwargs || {};

    if (value === "" && key in currentKwargs) {
      // Remove the key if value is empty
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [key]: _, ...rest } = currentKwargs;
      newChain[stepIndex] = { ...newChain[stepIndex], kwargs: rest };
    } else if (value !== "") {
      // Add or update the key
      newChain[stepIndex] = {
        ...newChain[stepIndex],
        kwargs: { ...currentKwargs, [key]: value },
      };
    }

    handleFieldChange("locator", {
      ...editedBlock.locator,
      chain: newChain,
    });
  };

  const handleAddKwargPair = (stepIndex: number) => {
    const newChain = [...editedBlock.locator.chain];
    const currentKwargs = newChain[stepIndex].kwargs || {};
    const newKey = `arg${Object.keys(currentKwargs).length + 1}`;

    newChain[stepIndex] = {
      ...newChain[stepIndex],
      kwargs: { ...currentKwargs, [newKey]: "" },
    };

    handleFieldChange("locator", {
      ...editedBlock.locator,
      chain: newChain,
    });
  };

  const handleAddLocatorStep = () => {
    const newStep: LocatorStep = {
      method: "",
      args: [],
      kwargs: {},
      attribute: "",
    };

    handleFieldChange("locator", {
      ...editedBlock.locator,
      chain: [...editedBlock.locator.chain, newStep],
    });
  };

  const handleRemoveLocatorStep = (stepIndex: number) => {
    const newChain = editedBlock.locator.chain.filter((_, index) => index !== stepIndex);
    handleFieldChange("locator", {
      ...editedBlock.locator,
      chain: newChain,
    });
  };

  const handleActionChange = (field: keyof ActionInstruction, value: unknown) => {
    console.log("handleActionChange:", field, value, "current action:", editedBlock.action);
    let newAction: ActionInstruction;

    if (editedBlock.action) {
      newAction = { ...editedBlock.action, [field]: value };
    } else {
      // Create new action with the specified field
      if (field === "action") {
        newAction = { action: value as ActionType };
      } else {
        newAction = { action: "click" as ActionType, [field]: value };
      }
    }

    console.log("New action object:", newAction);
    handleFieldChange("action", newAction);
  };

  const handleSave = () => {
    onUpdate?.(editedBlock);
  };

  // Reset editor state when block prop changes (render-time update, no effect needed)
  const [prevBlock, setPrevBlock] = useState(block);
  if (block !== prevBlock) {
    setPrevBlock(block);
    setEditedBlock(block);
    setIsModified(false);
  }

  return (
    <Box sx={{ pt: 1 }}>
      <TabContext value={blockTab}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "rgba(0, 0, 0, 0.12)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <TabList onChange={handleTabChange} aria-label="Block editor tabs">
            <Tab
              label="Definition"
              value="info"
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                textTransform: "none",
                minHeight: "40px",
              }}
            />
            <Tab
              label="Screenshots"
              value="screenshots"
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                textTransform: "none",
                minHeight: "40px",
              }}
            />
          </TabList>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={debugParams.debug === "true"}
                onChange={(e) => {
                  setDebugParam("debug", e.target.checked ? "true" : undefined);
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontSize: "14px" }}>
                Dev mode
              </Typography>
            }
            sx={{ mr: 2 }}
          />
        </Box>

        <TabPanel value="info">
          <PanelGroup direction="horizontal">
            {/* Left Panel - Locator Chain (40%) */}
            <Panel defaultSize={50} minSize={30} maxSize={60}>
              <Box sx={{ height: "100%" }}>
                <Stack spacing={1.5}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    justifyContent="space-between"
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "text.primary",
                      }}
                    >
                      Locator
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <TextField
                      fullWidth
                      label="Semantic Text"
                      value={editedBlock.locator.text || ""}
                      onChange={(e) =>
                        handleFieldChange("locator", {
                          ...editedBlock.locator,
                          text: e.target.value || undefined,
                        })
                      }
                      size="small"
                      multiline
                      minRows={1}
                      maxRows={3}
                      placeholder="Human-readable description of the element to locate"
                      sx={{
                        "& .MuiInputBase-input": { fontSize: "14px" },
                        "& .MuiInputLabel-root": {
                          fontSize: "14px",
                          "&.Mui-focused": { fontSize: "14px" },
                        },
                      }}
                    />

                    <Tooltip
                      title="When enabled, uses AI to locate elements based on the semantic text description instead of deterministic selectors"
                      arrow
                      placement="top"
                    >
                      <Switch
                        size="small"
                        checked={editedBlock.locator.strategy_used === "ai_based"}
                        onChange={(e) =>
                          handleFieldChange("locator", {
                            ...editedBlock.locator,
                            strategy_used: e.target.checked ? "ai_based" : undefined,
                          })
                        }
                      />
                    </Tooltip>
                  </Stack>

                  <Divider sx={{ my: 1 }} />

                  {editedBlock.locator.chain.map((step, index) => (
                    <Card
                      key={index}
                      variant="outlined"
                      sx={{
                        borderRadius: "6px",
                        border: "1px solid",
                        borderColor: "rgba(0, 0, 0, 0.12)",
                      }}
                    >
                      <CardContent sx={{ padding: "10px 12px !important" }}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{ mb: 1.5 }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "11px",
                              fontWeight: 600,
                              color: "text.secondary",
                            }}
                          >
                            Step {index + 1}
                          </Typography>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveLocatorStep(index)}
                            sx={{
                              padding: "4px",
                              "& .MuiSvgIcon-root": {
                                fontSize: "16px",
                              },
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Stack>

                        <Stack spacing={1.5}>
                          {/* Method/Attribute Selector */}
                          <Stack spacing={1} direction="row">
                            <FormControl size="small" sx={{ width: 180 }}>
                              <InputLabel
                                sx={{
                                  fontSize: "14px",
                                  "&.Mui-focused": { fontSize: "14px" },
                                }}
                              >
                                Type
                              </InputLabel>
                              <Select
                                sx={{
                                  fontSize: "14px",
                                  "& .MuiSelect-select": { fontSize: "14px" },
                                }}
                                value={isDefined(step.method) ? "method" : "attribute"}
                                onChange={(e) => {
                                  const newChain = [...editedBlock.locator.chain];
                                  if (e.target.value === "method") {
                                    newChain[index] = {
                                      ...newChain[index],
                                      method: "get_by_text",
                                      attribute: undefined,
                                      args: [],
                                      kwargs: {},
                                    };
                                  } else {
                                    newChain[index] = {
                                      ...newChain[index],
                                      method: undefined,
                                      attribute: "first",
                                      args: undefined,
                                      kwargs: undefined,
                                    };
                                  }

                                  handleFieldChange("locator", {
                                    ...editedBlock.locator,
                                    chain: newChain,
                                  });
                                }}
                                label="Type"
                              >
                                <MenuItem value="method" sx={{ fontSize: "14px" }}>
                                  {humanizeText("method")}
                                </MenuItem>
                                <MenuItem value="attribute" sx={{ fontSize: "14px" }}>
                                  {humanizeText("attribute")}
                                </MenuItem>
                              </Select>
                            </FormControl>

                            {isDefined(step.method) ? (
                              <FormControl fullWidth size="small">
                                <InputLabel
                                  sx={{
                                    fontSize: "14px",
                                    "&.Mui-focused": { fontSize: "14px" },
                                  }}
                                >
                                  Method
                                </InputLabel>
                                <Select
                                  sx={{
                                    fontSize: "14px",
                                    "& .MuiSelect-select": { fontSize: "14px" },
                                  }}
                                  value={step.method || ""}
                                  onChange={(e) =>
                                    handleLocatorStepChange(index, "method", e.target.value)
                                  }
                                  label="Method"
                                >
                                  <MenuItem value="locator" sx={{ fontSize: "14px" }}>
                                    Locator (CSS/XPath)
                                  </MenuItem>
                                  <MenuItem value="get_by_alt_text" sx={{ fontSize: "14px" }}>
                                    Get By Alt Text
                                  </MenuItem>
                                  <MenuItem value="get_by_label" sx={{ fontSize: "14px" }}>
                                    Get By Label
                                  </MenuItem>
                                  <MenuItem value="get_by_placeholder" sx={{ fontSize: "14px" }}>
                                    Get By Placeholder
                                  </MenuItem>
                                  <MenuItem value="get_by_role" sx={{ fontSize: "14px" }}>
                                    Get By Role
                                  </MenuItem>
                                  <MenuItem value="get_by_text" sx={{ fontSize: "14px" }}>
                                    Get By Text
                                  </MenuItem>
                                  <MenuItem value="get_by_title" sx={{ fontSize: "14px" }}>
                                    Get By Title
                                  </MenuItem>
                                  <MenuItem value="get_by_test_id" sx={{ fontSize: "14px" }}>
                                    Get By Test ID
                                  </MenuItem>
                                  <MenuItem value="frame_locator" sx={{ fontSize: "14px" }}>
                                    Frame Locator
                                  </MenuItem>
                                  <MenuItem value="filter" sx={{ fontSize: "14px" }}>
                                    Filter
                                  </MenuItem>
                                  <MenuItem value="first" sx={{ fontSize: "14px" }}>
                                    First
                                  </MenuItem>
                                  <MenuItem value="last" sx={{ fontSize: "14px" }}>
                                    Last
                                  </MenuItem>
                                  <MenuItem value="nth" sx={{ fontSize: "14px" }}>
                                    Nth
                                  </MenuItem>
                                </Select>
                              </FormControl>
                            ) : (
                              <TextField
                                fullWidth
                                label={humanizeText("attribute")}
                                value={step.attribute || ""}
                                onChange={(e) =>
                                  handleLocatorStepChange(index, "attribute", e.target.value)
                                }
                                size="small"
                                sx={{
                                  "& .MuiInputBase-input": { fontSize: "14px" },
                                  "& .MuiInputLabel-root": {
                                    fontSize: "14px",
                                    "&.Mui-focused": { fontSize: "14px" },
                                  },
                                }}
                              />
                            )}
                          </Stack>

                          {isDefined(step.method) && (
                            <Stack spacing={1}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                justifyContent="space-between"
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "12px",
                                    fontWeight: 500,
                                    color: "text.primary",
                                    pb: 0.5,
                                  }}
                                >
                                  Arguments
                                </Typography>
                                <Tooltip title="Add Argument">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      const newArgs = [...(step.args || []), ""];
                                      handleLocatorStepChange(index, "args", newArgs);
                                    }}
                                    color="primary"
                                    sx={{
                                      padding: "4px",
                                      "& .MuiSvgIcon-root": {
                                        fontSize: "16px",
                                      },
                                    }}
                                  >
                                    <AddIcon />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                              {(!step.args || step.args.length === 0) && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: "11px",
                                    color: "text.secondary",
                                  }}
                                >
                                  No arguments. Click the + button to add.
                                </Typography>
                              )}
                              <Stack spacing={1.5}>
                                {(step.args || []).map((arg, argIndex) => (
                                  <Stack key={argIndex} direction="row" spacing={1}>
                                    <TextField
                                      fullWidth
                                      label={`Argument ${argIndex + 1}`}
                                      value={typeof arg === "string" ? arg : JSON.stringify(arg)}
                                      onChange={(e) => {
                                        const newArgs = [...(step.args || [])];
                                        let value: unknown = e.target.value;
                                        try {
                                          value = JSON.parse(e.target.value);
                                        } catch {
                                          // Keep as string
                                        }
                                        newArgs[argIndex] = value;
                                        handleLocatorStepChange(index, "args", newArgs);
                                      }}
                                      size="small"
                                      sx={{
                                        "& .MuiInputBase-input": {
                                          fontSize: "14px",
                                        },
                                        "& .MuiInputLabel-root": {
                                          fontSize: "14px",
                                          "&.Mui-focused": { fontSize: "14px" },
                                        },
                                      }}
                                    />
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => {
                                        const newArgs = [...(step.args || [])];
                                        newArgs.splice(argIndex, 1);
                                        handleLocatorStepChange(index, "args", newArgs);
                                      }}
                                      sx={{
                                        padding: "4px",
                                        "& .MuiSvgIcon-root": {
                                          fontSize: "16px",
                                        },
                                      }}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Stack>
                                ))}
                              </Stack>

                              {/* Named Arguments key-value pairs */}
                              <Stack
                                spacing={1}
                                alignItems="center"
                                direction="row"
                                justifyContent="space-between"
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "12px",
                                    fontWeight: 500,
                                    color: "text.primary",
                                    pb: 0.5,
                                  }}
                                >
                                  Named Arguments (Key-Value pairs)
                                </Typography>
                                <Tooltip title="Add Named Argument">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleAddKwargPair(index)}
                                    color="primary"
                                    sx={{
                                      padding: "4px",
                                      "& .MuiSvgIcon-root": {
                                        fontSize: "16px",
                                      },
                                    }}
                                  >
                                    <AddIcon />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                              {step.kwargs && Object.keys(step.kwargs).length === 0 && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: "11px",
                                    color: "text.secondary",
                                  }}
                                >
                                  No named arguments. Click the + button to add.
                                </Typography>
                              )}
                              <Stack spacing={1.5}>
                                {Object.entries(step.kwargs || {}).map(([key, value], kvIndex) => (
                                  <Stack key={kvIndex} direction="row" spacing={1}>
                                    <TextField
                                      label="Key"
                                      value={key}
                                      onChange={(e) => {
                                        // Remove old key and add new key
                                        const newChain = [...editedBlock.locator.chain];
                                        const currentKwargs = {
                                          ...newChain[index].kwargs,
                                        };
                                        delete currentKwargs[key];
                                        currentKwargs[e.target.value] = value;
                                        newChain[index] = {
                                          ...newChain[index],
                                          kwargs: currentKwargs,
                                        };
                                        handleFieldChange("locator", {
                                          ...editedBlock.locator,
                                          chain: newChain,
                                        });
                                      }}
                                      size="small"
                                      sx={{
                                        flex: 1,
                                        "& .MuiInputBase-input": {
                                          fontSize: "14px",
                                        },
                                        "& .MuiInputLabel-root": {
                                          fontSize: "14px",
                                          "&.Mui-focused": {
                                            fontSize: "14px",
                                          },
                                        },
                                      }}
                                    />
                                    <TextField
                                      label="Value"
                                      value={
                                        typeof value === "string" ? value : JSON.stringify(value)
                                      }
                                      onChange={(e) =>
                                        handleKwargsChange(index, key, e.target.value)
                                      }
                                      size="small"
                                      sx={{
                                        flex: 2,
                                        "& .MuiInputBase-input": {
                                          fontSize: "14px",
                                        },
                                        "& .MuiInputLabel-root": {
                                          fontSize: "14px",
                                          "&.Mui-focused": {
                                            fontSize: "14px",
                                          },
                                        },
                                      }}
                                    />
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleKwargsChange(index, key, "")}
                                      sx={{
                                        padding: "4px",
                                        "& .MuiSvgIcon-root": {
                                          fontSize: "16px",
                                        },
                                      }}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Stack>
                                ))}
                              </Stack>
                            </Stack>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}

                  <Button
                    variant="outlined"
                    startIcon={<AddIcon sx={{ fontSize: "16px" }} />}
                    onClick={handleAddLocatorStep}
                    sx={{
                      alignSelf: "flex-start",
                      fontSize: "14px",
                      fontWeight: 500,
                      textTransform: "none",
                      padding: "6px 12px",
                      borderColor: "rgba(0, 0, 0, 0.12)",
                      "&:hover": {
                        borderColor: "rgba(0, 0, 0, 0.2)",
                        backgroundColor: "rgba(0, 0, 0, 0.02)",
                      },
                    }}
                  >
                    Add Step
                  </Button>
                </Stack>
              </Box>
            </Panel>

            <PanelResizeHandle>
              <Divider orientation="vertical">
                <MultipleStopIcon color="disabled" fontSize="small" />
              </Divider>
            </PanelResizeHandle>

            {/* Right Panel - Action Configuration (60%) */}
            <Panel defaultSize={50} minSize={40} maxSize={70}>
              <Box sx={{ height: "100%" }}>
                <Stack spacing={1.5}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "text.primary",
                    }}
                  >
                    Action
                  </Typography>

                  <Autocomplete
                    fullWidth
                    size="small"
                    options={ACTION_INSTRUCTION_ACTIONS}
                    value={editedBlock.action?.action || null}
                    onChange={(_, value) => {
                      console.log("Action autocomplete onChange:", value);
                      if (value) {
                        handleActionChange("action", value as ActionType);
                      }
                    }}
                    renderOption={(props, option) => (
                      <Typography {...props} variant="body2" sx={{ fontSize: "14px" }}>
                        {getActionLabel(option)}
                      </Typography>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select an action..."
                        label="Action Type"
                        size="small"
                        sx={{
                          "& .MuiInputBase-input": { fontSize: "14px" },
                          "& .MuiInputLabel-root": {
                            fontSize: "14px",
                            "&.Mui-focused": { fontSize: "14px" },
                          },
                        }}
                      />
                    )}
                    getOptionLabel={(option) => getActionLabel(option)}
                    autoComplete
                    includeInputInList
                    filterSelectedOptions
                    clearOnBlur
                    handleHomeEndKeys
                  />

                  {/* Dynamic action fields based on action type */}
                  <ActionSelector
                    action={editedBlock.action}
                    onActionChange={handleActionChange}
                    appConfigId={appConfigId}
                  />

                  <TextField
                    fullWidth
                    label="Timeout (ms)"
                    type="number"
                    value={editedBlock.action?.timeout || ""}
                    onChange={(e) =>
                      handleActionChange("timeout", parseInt(e.target.value, 10) || undefined)
                    }
                    size="small"
                    placeholder="Optional timeout in milliseconds"
                    sx={{
                      "& .MuiInputBase-input": { fontSize: "14px" },
                      "& .MuiInputLabel-root": {
                        fontSize: "14px",
                        "&.Mui-focused": { fontSize: "14px" },
                      },
                    }}
                  />
                </Stack>
              </Box>
            </Panel>
          </PanelGroup>

          {/* Block Configuration - Full Width at Bottom */}
          <Box sx={{ mt: 2 }}>
            <Accordion
              sx={{
                borderRadius: "6px",
                border: "1px solid",
                borderColor: "rgba(0, 0, 0, 0.12)",
                boxShadow: "none",
                "&:before": {
                  display: "none",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ fontSize: "18px" }} />}
                sx={{
                  backgroundColor: "grey.50",
                  borderRadius: "8px 8px 0 0",
                  minHeight: "40px",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "text.primary",
                  }}
                >
                  Advanced Configuration
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 2 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: "6px",
                        border: "1px solid",
                        borderColor: "rgba(0, 0, 0, 0.12)",
                      }}
                    >
                      <CardContent sx={{ padding: "10px 12px !important" }}>
                        <Editor
                          height={`${Math.max(editedBlock.code.length / 5, 80)}px`}
                          defaultLanguage="python"
                          value={editedBlock.code}
                          options={{
                            readOnly: true,
                            minimap: { enabled: false },
                            scrollBeyondLastLine: false,
                            lineNumbers: "off",
                            wordWrap: "on",
                            wrappingIndent: "same",
                            fontSize: 12,
                          }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Grid container spacing={0}>
                      <Grid size={{ xs: 12, sm: 12 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={editedBlock.should_fail || false}
                              onChange={(e) => handleFieldChange("should_fail", e.target.checked)}
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ fontSize: "14px" }}>
                              Should Fail (makes the test fail if this block succeeds)
                            </Typography>
                          }
                        />
                      </Grid>

                      <Grid size={{ xs: 12, sm: 12 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={editedBlock.should_fix_with_ai || false}
                              onChange={(e) =>
                                handleFieldChange("should_fix_with_ai", e.target.checked)
                              }
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ fontSize: "14px" }}>
                              Try to fix step with AI
                            </Typography>
                          }
                        />
                      </Grid>

                      <Grid size={{ xs: 12, sm: 12 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={editedBlock.should_try_deterministic || false}
                              onChange={(e) =>
                                handleFieldChange("should_try_deterministic", e.target.checked)
                              }
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ fontSize: "14px" }}>
                              Try to generate deterministic step
                            </Typography>
                          }
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <Stack spacing={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel
                          sx={{
                            fontSize: "14px",
                            "&.Mui-focused": { fontSize: "14px" },
                          }}
                        >
                          AAA Phase
                        </InputLabel>
                        <Select
                          value={editedBlock.aaa_phase || ""}
                          onChange={(e) =>
                            handleFieldChange("aaa_phase", e.target.value || undefined)
                          }
                          label="AAA Phase"
                          sx={{
                            fontSize: "14px",
                            "& .MuiSelect-select": { fontSize: "14px" },
                          }}
                        >
                          <MenuItem value="" sx={{ fontSize: "14px" }}>
                            None
                          </MenuItem>
                          {aaaPhases.map((phase) => (
                            <MenuItem key={phase} value={phase} sx={{ fontSize: "14px" }}>
                              {humanizeText(phase)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <TextField
                        fullWidth
                        label="Original Task"
                        value={editedBlock.original_task || ""}
                        onChange={(e) =>
                          handleFieldChange("original_task", e.target.value || undefined)
                        }
                        size="small"
                        multiline
                        minRows={3}
                        maxRows={10}
                        placeholder="Describe the original task this block was created from"
                        sx={{
                          "& .MuiInputBase-input": { fontSize: "14px" },
                          "& .MuiInputLabel-root": {
                            fontSize: "14px",
                            "&.Mui-focused": { fontSize: "14px" },
                          },
                        }}
                      />

                      <TextField
                        fullWidth
                        label="Group"
                        value={editedBlock.group || ""}
                        onChange={(e) => handleFieldChange("group", e.target.value || undefined)}
                        size="small"
                        placeholder="Optional group name"
                        helperText="Steps with same group will be skipped if group check fails"
                        sx={{
                          "& .MuiInputBase-input": { fontSize: "14px" },
                          "& .MuiInputLabel-root": {
                            fontSize: "14px",
                            "&.Mui-focused": { fontSize: "14px" },
                          },
                          "& .MuiFormHelperText-root": { fontSize: "12px" },
                        }}
                      />

                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={editedBlock.is_group_check || false}
                            onChange={(e) => handleFieldChange("is_group_check", e.target.checked)}
                            size="small"
                          />
                        }
                        label="Is Group Check"
                        sx={{
                          "& .MuiFormControlLabel-label": { fontSize: "14px" },
                        }}
                      />
                    </Stack>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Box>
        </TabPanel>

        <TabPanel value="screenshots">
          <Stack spacing={3} alignItems="center">
            {editedBlock.pre_screenshot_path || editedBlock.post_screenshot_path ? (
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="space-around"
                width="100%"
              >
                {editedBlock.pre_screenshot_path && (
                  <Stack spacing={1} alignItems="center">
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "text.primary",
                      }}
                    >
                      Before Execution
                    </Typography>
                    <ImageFromPath path={editedBlock.pre_screenshot_path} height={300} />
                  </Stack>
                )}

                {editedBlock.post_screenshot_path && (
                  <Stack spacing={1} alignItems="center">
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "text.primary",
                      }}
                    >
                      After Execution
                    </Typography>
                    <ImageFromPath path={editedBlock.post_screenshot_path} height={300} />
                  </Stack>
                )}
              </Stack>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  fontSize: "14px",
                  color: "text.secondary",
                }}
              >
                No screenshots available for this block
              </Typography>
            )}
          </Stack>
        </TabPanel>
      </TabContext>

      {/* Save Button at Bottom */}
      {isModified && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: 2,
            pt: 2,
            borderTop: 1,
            borderColor: "rgba(0, 0, 0, 0.12)",
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            startIcon={<SaveIcon sx={{ fontSize: "16px" }} />}
            sx={{
              fontSize: "14px",
              fontWeight: 500,
              textTransform: "none",
              padding: "8px 16px",
            }}
          >
            Save Changes
          </Button>
        </Box>
      )}
    </Box>
  );
};
